import { Modal, Select, Tag, Button, Divider, message, Spin } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import { forwardRef, useImperativeHandle, useState } from "react";
import DatePicker from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import api from "../../api/api";
import { SCENARIO_OPTIONS, SCENARIO_MAP } from "./scenarios";

const STATUS_OPTIONS = [
  { label: "پاسخگو نبود", value: "non_responsive", color: "purple" },
  { label: "عدم امکان پاسخگویی", value: "unable_to_respond", color: "purple" },
  { label: "کنسل", value: "cancelled", color: "red" },
  { label: "خریداری شده بود", value: "already_purchased", color: "red" },
  { label: "ارسال لینک", value: "link_sent", color: "gold" },
  { label: "تهیه محصول", value: "purchased", color: "green" },
  { label: "تماس پیگیری انجام شد", value: "follow_up_done", color: "gold" },
  { label: "تماس مجدد", value: "recall", color: "orange" },
  { label: "پایان پیگیری", value: "follow_up_ended", color: "blue" },
];

const STATUS_MAP = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s]));

function LeadReportModal({ onReportChange, disableNewReport = false }, ref) {
  const [open, setOpen] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [leadOperatorId, setLeadOperatorId] = useState(null);
  const [username, setUsername] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [status, setStatus] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [note, setNote] = useState("");
  const [calledAt, setCalledAt] = useState(null);

  useImperativeHandle(ref, () => ({
    open: (id, mobile, operatorId) => {
      setLeadId(id);
      setLeadOperatorId(operatorId ?? null);
      setUsername(mobile);
      setOpen(true);
      loadReports(id);
    },
  }));

  const loadReports = (id) => {
    setLoading(true);
    api
      .get(`/lead-reports/${id}`)
      .then((res) => setReports(res.data || []))
      .catch(() => message.error("خطا در دریافت گزارش‌ها"))
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setStatus(null);
    setScenario(null);
    setNote("");
    setCalledAt(null);
    setEditingId(null);
  };

  const closeHandler = () => {
    setOpen(false);
    resetForm();
    setReports([]);
    setLeadId(null);
    setLeadOperatorId(null);
    setUsername(null);
  };

  const submitReport = () => {
    if (disableNewReport && !editingId) {
      return;
    }

    if (!status) {
      message.warning("لطفا وضعیت را انتخاب کنید");
      return;
    }

    const payload = { status, note, calledAt, scenario: scenario ?? null };

    if (editingId) {
      api
        .put(`/lead-reports/${editingId}`, payload)
        .then(() => {
          message.success("گزارش ویرایش شد");
          loadReports(leadId);
          resetForm();
        })
        .catch(() => message.error("خطا در ویرایش گزارش"));
    } else {
      api
        .post("/lead-reports", { ...payload, leadId, leadOperatorId })
        .then(() => {
          message.success("گزارش ثبت شد");
          loadReports(leadId);
          resetForm();
          onReportChange?.(leadId, calledAt || new Date(), scenario ?? null);
        })
        .catch(() => message.error("خطا در ثبت گزارش"));
    }
  };

  const startEdit = (report) => {
    setEditingId(report.id);
    setStatus(report.status);
    setScenario(report.scenario || null);
    setNote(report.note || "");
    setCalledAt(report.calledAt ? new Date(report.calledAt) : null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("fa-IR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Modal
      title="گزارش تماس‌ها"
      open={open}
      onCancel={closeHandler}
      footer={null}
      width={600}
      destroyOnClose
    >
      {username && (
        <div style={{ marginBottom: 12 }}>
          <span
            onClick={() => {
              navigator.clipboard.writeText(username);
              message.success("شماره موبایل کپی شد");
            }}
            style={{
              cursor: "pointer",
              fontSize: 15,
              fontWeight: 500,
              color: "#1890ff",
              direction: "ltr",
              display: "inline-block",
            }}
          >
            {username}
          </span>
        </div>
      )}
      <Spin spinning={loading}>
        {reports.length > 0 && (
          <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 16 }}>
            {reports.map((report) => (
              <div
                key={report.id}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 8,
                  backgroundColor:
                    editingId === report.id ? "#fafafa" : "white",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 4,
                  }}
                >
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <Tag color={STATUS_MAP[report.status]?.color || "default"}>
                      {STATUS_MAP[report.status]?.label || report.status}
                    </Tag>
                    {report.scenario && (
                      <Tag
                        color={
                          SCENARIO_MAP[report.scenario]?.color || "default"
                        }
                      >
                        {SCENARIO_MAP[report.scenario]?.label ||
                          report.scenario}
                      </Tag>
                    )}
                    <span style={{ fontSize: 12, color: "#999" }}>
                      {formatDate(report.calledAt || report.createdAt)}
                    </span>
                  </div>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => startEdit(report)}
                  />
                </div>
                {report.note && (
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: 13,
                      color: "#555",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {report.note}
                  </p>
                )}
                {report.operator && (
                  <span style={{ fontSize: 11, color: "#aaa" }}>
                    {report.operator.firstname} {report.operator.lastname}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {reports.length === 0 && !loading && (
          <p style={{ textAlign: "center", color: "#999", margin: "16px 0" }}>
            هنوز گزارشی ثبت نشده
          </p>
        )}
      </Spin>

      {(!disableNewReport || editingId) && (
        <>
          <Divider style={{ margin: "12px 0" }} />

          <h4 style={{ marginBottom: 8 }}>
            {editingId ? "ویرایش گزارش" : "ثبت گزارش جدید"}
          </h4>

          <div style={{ display: "flex", gap: "1rem", marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <p style={{ marginBottom: 4, fontSize: 13 }}>وضعیت:</p>
              <Select
                style={{ width: "100%" }}
                placeholder="انتخاب وضعیت"
                value={status}
                onChange={setStatus}
                options={STATUS_OPTIONS.map((s) => ({
                  label: s.label,
                  value: s.value,
                }))}
              />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ marginBottom: 4, fontSize: 13 }}>سناریو:</p>
              <Select
                style={{ width: "100%" }}
                placeholder="انتخاب سناریو"
                value={scenario}
                onChange={(value) => setScenario(value ?? null)}
                allowClear
                options={SCENARIO_OPTIONS.map((s) => ({
                  label: s.label,
                  value: s.value,
                }))}
              />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ marginBottom: 4, fontSize: 13 }}>زمان تماس:</p>
              <DatePicker
                style={{ width: "100%" }}
                format="HH:mm - YYYY/MM/DD"
                calendar={persian}
                locale={persian_fa}
                value={calledAt}
                onChange={(date) => {
                  setCalledAt(date?.toDate?.() || null);
                }}
                plugins={[
                  <TimePicker style={{ width: "100%" }} position="left" />,
                ]}
              />
            </div>
          </div>

          <p style={{ marginBottom: 4, fontSize: 13 }}>توضیحات:</p>
          <TextArea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="توضیحات تماس..."
          />

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 12,
            }}
          >
            {editingId && <Button onClick={resetForm}>انصراف</Button>}
            <Button
              type="primary"
              icon={editingId ? <EditOutlined /> : <PlusOutlined />}
              onClick={submitReport}
            >
              {editingId ? "ویرایش" : "ثبت گزارش"}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

export default forwardRef(LeadReportModal);
export { STATUS_OPTIONS, STATUS_MAP };
